import React, { createContext, useContext, useReducer, useEffect, useMemo } from 'react';
import { appReducer, getBlock } from '../engine/appReducer';
import { useProjectConfig } from '../hooks/useProjectConfig';
import { loadJSON, saveJSON, removeKeys } from '../utils/storage';
import { STORAGE_KEYS, INITIAL_VARIABLES } from '../config/appConfig';

const AppStoreContext = createContext(null);

// Гидрация состояния из localStorage (прежние ключи — прогресс пользователя сохраняется).
const initState = () => {
  const persisted = loadJSON(STORAGE_KEYS.state, {});
  return {
    config: null,
    currentBlockId: persisted.currentBlockId || null,
    showSummary: persisted.showSummary || false,
    viewingPhraseIndex: persisted.viewingPhraseIndex || 0,
    variables: loadJSON(STORAGE_KEYS.variables, { ...INITIAL_VARIABLES }),
    history: loadJSON(STORAGE_KEYS.history, []),
  };
};

export const AppStoreProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, undefined, initState);
  const { data: config, loading, error } = useProjectConfig();

  // Конфиг загрузился → кладём в стор (там же решается стартовый экран).
  useEffect(() => {
    if (config) dispatch({ type: 'SET_CONFIG', config });
  }, [config]);

  // Персист слайсов состояния в localStorage.
  useEffect(() => {
    saveJSON(STORAGE_KEYS.state, {
      currentBlockId: state.currentBlockId,
      showSummary: state.showSummary,
      viewingPhraseIndex: state.viewingPhraseIndex,
    });
  }, [state.currentBlockId, state.showSummary, state.viewingPhraseIndex]);

  useEffect(() => {
    saveJSON(STORAGE_KEYS.history, state.history);
  }, [state.history]);

  useEffect(() => {
    saveJSON(STORAGE_KEYS.variables, state.variables);
  }, [state.variables]);

  // Стабильные экшены потока.
  const actions = useMemo(
    () => ({
      blockNext: (value, nextId, skip = false) =>
        dispatch({ type: 'BLOCK_NEXT', value, nextId, skip }),
      conditionNext: (nextId) => dispatch({ type: 'CONDITION_NEXT', nextId }),
      nextPhrase: () => dispatch({ type: 'NEXT_PHRASE' }),
      selectPhrase: (index) => dispatch({ type: 'SELECT_PHRASE', index }),
      askAssistant: (payload) => dispatch({ type: 'ASK_ASSISTANT', payload }),
      restart: () => {
        removeKeys(STORAGE_KEYS.state, STORAGE_KEYS.history, STORAGE_KEYS.variables);
        dispatch({ type: 'RESTART' });
      },
    }),
    []
  );

  const currentBlock = getBlock(state, state.currentBlockId);

  const value = useMemo(
    () => ({
      state,
      dispatch,
      actions,
      currentBlock,
      config: state.config,
      loading,
      error,
    }),
    [state, actions, currentBlock, loading, error]
  );

  return <AppStoreContext.Provider value={value}>{children}</AppStoreContext.Provider>;
};

/**
 * Доступ к единому стору приложения. Здесь же будущий AI-контроллер сможет читать
 * текущий шаг (currentBlock), переменные и историю, и вызывать actions для перехода.
 */
export const useAppStore = () => {
  const ctx = useContext(AppStoreContext);
  if (!ctx) {
    throw new Error('useAppStore must be used within AppStoreProvider');
  }
  return ctx;
};
